/**
 * AuthFormCard
 * 
 * Single source of truth for the tabbed login/signup form.
 * Used on the Auth page AND inside modals (paywall, favorites, guest exit).
 * Includes social login, email forms, password reset dialog, legal modals.
 */

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { TABS, SPACING, TYPOGRAPHY, RESPONSIVE, DIALOGS } from '@/design-system';
import { z } from 'zod';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ValidatedInput } from '@/components/FormValidation';
import { PasswordStrengthIndicator } from '@/components/PasswordStrengthIndicator';
import { SocialLoginButtons } from '@/components/SocialLoginButtons';
import { LegalDocumentModal } from '@/components/LegalDocumentModal';
import { LAuth, LCommon } from '@/components/i18n';
import { useLocalizations } from '@/hooks/useLocalizations';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { useUserContext } from '@/contexts/UserContext';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { useAnalyticsTracking } from '@/hooks/useAnalyticsTracking';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';

interface AuthFormCardProps {
  /** Which tab to show initially */
  initialTab?: 'login' | 'signup';
  /** Session ID to store before OAuth redirect (guest chat flow) */
  sessionId?: string | null;
  /** Called after successful login/signup (modals use this to close) */
  onSuccess?: () => void;
  /** Called when signup succeeds and email verification is needed (Auth page shows verification screen) */
  onVerificationNeeded?: (email: string) => void;
  /** Additional className for the outer Card */
  className?: string;
}

export function AuthFormCard({
  initialTab = 'login',
  sessionId,
  onSuccess,
  onVerificationNeeded,
  className,
}: AuthFormCardProps) {
  const { signIn, signUp, resetPassword, resendConfirmationEmail } = useAuth();
  const { lAuth, lCommon } = useLocalizations();
  const { settings } = useLocale();
  const { currentLocation } = useUserContext();
  const { trackAuth } = useAnalyticsTracking();
  const { executeRecaptcha, verifyToken, isConfigured: recaptchaConfigured } = useRecaptcha();

  // Schemas
  const emailSchema = z.string()
    .min(1, lCommon('validation.email_required', { defaultValue: 'Email is required. Please enter an email address.' }))
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, lCommon('validation.invalid_email', { defaultValue: 'Invalid email address.' }))
    .max(255);
  const passwordSchema = z.string().min(8, lCommon('validation.password_min_length', { defaultValue: 'Password must be at least 8 characters.' })).max(100);
  const nameSchema = z.string().trim().min(2, lCommon('validation.name_min_length', { defaultValue: 'Name must be at least 2 characters.' })).max(100);

  // Tab state
  const [activeTab, setActiveTab] = useState<string>(initialTab);

  // Shared
  const [isLoading, setIsLoading] = useState(false);
  const [loginFormError, setLoginFormError] = useState('');
  const [signupFormError, setSignupFormError] = useState('');
  const [showEmailConfirmAlert, setShowEmailConfirmAlert] = useState(false);
  const [lastAttemptedEmail, setLastAttemptedEmail] = useState('');

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginErrors, setLoginErrors] = useState<Record<string, string>>({});
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // Keep me signed in
  const [keepMeSignedIn, setKeepMeSignedIn] = useState(true);

  // Signup form
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupFullName, setSignupFullName] = useState('');
  const [signupErrors, setSignupErrors] = useState<Record<string, string>>({});
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [signupPasswordTouched, setSignupPasswordTouched] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Password reset
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState('');
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isResetLoading, setIsResetLoading] = useState(false);

  // Legal modals
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);

  // Store session ID before OAuth redirect
  const storeSessionBeforeAuth = () => {
    if (sessionId) {
      sessionStorage.setItem('pendingGuestSessionId', sessionId);
    }
  };

  // reCAPTCHA helper
  const verifyRecaptchaAction = async (action: string): Promise<boolean> => {
    if (!recaptchaConfigured) return true;
    const token = await executeRecaptcha(action);
    if (token) {
      const result = await verifyToken(token, action);
      if (result.success && result.score && result.score < 0.5) {
        toast.error(lAuth('login.toast.verification_failed', { defaultValue: 'Verification failed. Please try again.' }), { id: 'auth-recaptcha' });
        return false;
      }
    }
    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginFormError('');
    setIsLoading(true);
    try {
      const normalizedEmail = loginEmail.trim().toLowerCase();
      emailSchema.parse(normalizedEmail);
      passwordSchema.parse(loginPassword);
      storeSessionBeforeAuth();

      if (!(await verifyRecaptchaAction('login'))) {
        setIsLoading(false);
        return;
      }

      const { error } = await signIn(normalizedEmail, loginPassword);
      if (error) {
        setLastAttemptedEmail(normalizedEmail);
        if (error.message.includes('Invalid login credentials')) {
          setLoginFormError(lAuth('login.toast.invalid_credentials', { defaultValue: 'Invalid email or password.' }));
        } else if (error.message.includes('Email not confirmed')) {
          setShowEmailConfirmAlert(true);
          setLoginFormError(lAuth('login.toast.email_not_confirmed', { defaultValue: 'Your email has not been confirmed.' }));
        } else {
          setLoginFormError(lAuth('login.toast.failed', { defaultValue: 'Login failed.' }));
        }
      } else {
        // If "Keep me signed in" is unchecked, clear persisted session on browser close
        if (!keepMeSignedIn) {
          const handleBeforeUnload = () => {
            supabase.auth.signOut();
            window.removeEventListener('beforeunload', handleBeforeUnload);
          };
          window.addEventListener('beforeunload', handleBeforeUnload);
        }
        toast.success(lAuth('login.toast.success', { defaultValue: 'You have successfully logged in.' }), { id: 'auth-signin' });
        setShowEmailConfirmAlert(false);
        onSuccess?.();
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        setLoginFormError(error.errors[0].message);
      } else {
        setLoginFormError(lCommon('toast.unexpected.error', { defaultValue: 'Something went wrong.' }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupFormError('');
    if (!agreedToTerms) {
      setSignupFormError(lCommon('validation.terms_agreement_required.error', { defaultValue: 'Please agree to the terms.' }));
      return;
    }
    setIsLoading(true);
    try {
      const normalizedEmail = signupEmail.trim().toLowerCase();
      emailSchema.parse(normalizedEmail);
      passwordSchema.parse(signupPassword);
      nameSchema.parse(signupFullName);
      storeSessionBeforeAuth();

      if (!(await verifyRecaptchaAction('signup'))) {
        setIsLoading(false);
        return;
      }

      const { error, data } = await signUp(normalizedEmail, signupPassword, signupFullName, {
        locale: settings.locale,
        country: settings.country,
        detected_location_id: currentLocation && currentLocation !== '0' ? currentLocation : undefined,
      });

      if (error) {
        if (error.message.includes('already registered')) {
          setSignupFormError(lAuth('signup.error_already_registered.error', { defaultValue: 'This email is already registered.' }));
        } else {
          setSignupFormError(error.message || lAuth('signup.error_failed.error', { defaultValue: 'Sign up failed. Please try again.' }));
        }
      } else if (data?.user?.identities?.length === 0) {
        // Supabase returns no error but empty identities for existing emails (anti-enumeration)
        setSignupFormError(lAuth('signup.error_already_registered.error', { defaultValue: 'This email is already registered.' }));
      } else {
        trackAuth('signup');
        if (onVerificationNeeded) {
          onVerificationNeeded(normalizedEmail);
        } else {
          toast.success(lAuth('signup.toast.success', { defaultValue: 'Check your email to confirm your account.' }), { id: 'auth-signup' });
          onSuccess?.();
        }
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        setSignupFormError(error.errors[0].message);
      } else {
        setSignupFormError(lCommon('toast.unexpected.error', { defaultValue: 'Something went wrong.' }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const normalizedEmail = resetEmail.trim().toLowerCase();
    const validationResult = emailSchema.safeParse(normalizedEmail);
    if (!validationResult.success) {
      setResetError(validationResult.error.errors[0].message);
      return;
    }
    setResetError('');
    setIsResetLoading(true);
    try {
      // WP bridge is already called inside resetPassword() — no duplicate call needed

      const { error } = await resetPassword(normalizedEmail);
      if (error) {
        if (error.message?.includes('security purposes') || error.status === 429) {
          toast.error(error.message, { id: 'auth-reset' });
        } else {
          toast.error(lAuth('reset.toast.failed', { defaultValue: 'Password reset failed' }), { id: 'auth-reset' });
        }
      } else {
        trackAuth('password_reset');
        toast.success(lAuth('reset.toast.success', { defaultValue: 'Password reset link sent.' }), { id: 'auth-reset' });
        setIsResetDialogOpen(false);
        setResetEmail('');
        setResetError('');
      }
    } catch {
      toast.error(lCommon('toast.unexpected.error', { defaultValue: 'Something went wrong.' }), { id: 'auth-reset' });
    } finally {
      setIsResetLoading(false);
    }
  };

  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResendConfirmation = async () => {
    if (resendCooldown > 0) return;
    const emailToUse = lastAttemptedEmail.trim().toLowerCase();
    if (!emailToUse) {
      toast.error(lAuth('verification.toast.enter_email_first', { defaultValue: 'Please enter your email address first.' }), { id: 'auth-resend' });
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await resendConfirmationEmail(emailToUse);
      if (error) {
        toast.error(lAuth('verification.toast.resend_failed', { defaultValue: 'Failed to resend verification email.' }), { id: 'auth-resend' });
      } else {
        toast.success(lAuth('verification.toast.resend.success', { defaultValue: 'Verification email sent' }), { id: 'auth-resend' });
        setShowEmailConfirmAlert(false);
        setResendCooldown(60);
      }
    } catch {
      toast.error(lCommon('toast.unexpected.error', { defaultValue: 'Something went wrong.' }), { id: 'auth-resend' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card className={cn('p-5 shadow-xl overflow-hidden min-w-0', className)}>
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setLoginFormError(''); setSignupFormError(''); }} className="w-full">
          <TabsList className={TABS.listGrid2}>
            <TabsTrigger value="login" className={TABS.triggerGrid}>
              <LAuth id="login.title" defaultValue="Sign In" />
            </TabsTrigger>
            <TabsTrigger value="signup" className={TABS.triggerGrid}>
              <LAuth id="signup.title" defaultValue="Sign Up" />
            </TabsTrigger>
          </TabsList>

          {/* LOGIN TAB */}
          <TabsContent value="login" className={TABS.contentTopLevel}>
            <div onClick={storeSessionBeforeAuth}>
              <SocialLoginButtons className="mb-2" />
            </div>

            {showEmailConfirmAlert && (
              <Alert variant="info" className={cn(SPACING.sectionHeader)}>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex flex-col gap-1">
                  <span className="text-sm">
                    <LAuth id="login.need_confirm.description" defaultValue="Please confirm your email address to proceed with logging in." />
                  </span>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleResendConfirmation}
                    disabled={isLoading || resendCooldown > 0}
                    className="h-auto p-0 text-primary justify-start"
                  >
                    {resendCooldown > 0
                      ? `${lAuth('login.resend_confirmation', { defaultValue: 'Resend Confirmation Email' })} (${resendCooldown}s)`
                      : <LAuth id="login.resend_confirmation" defaultValue="Resend Confirmation Email" />
                    }
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleLogin} className={cn(SPACING.stackLg)}>
              <div className={cn(SPACING.formFieldGap)}>
                <Label htmlFor="auth-login-email">
                  <LAuth id="login.email.label" defaultValue="Email Address" />
                </Label>
                <ValidatedInput
                  id="auth-login-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder={lAuth('login.email.placeholder', { defaultValue: 'Enter email' })}
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  onBlur={(e) => {
                    const result = emailSchema.safeParse(e.target.value);
                    setLoginErrors((prev) => ({
                      ...prev,
                      email: result.success ? '' : result.error.errors[0].message,
                    }));
                  }}
                  disabled={isLoading}
                  error={loginErrors.email}
                  required
                />
              </div>

              <div className={cn(SPACING.formFieldGap)}>
                <div className={RESPONSIVE.flexRowBetween}>
                  <Label htmlFor="auth-login-password">
                    <LAuth id="login.password.label" defaultValue="Password" />
                  </Label>
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs"
                    type="button"
                    onClick={() => setIsResetDialogOpen(true)}
                  >
                    <LAuth id="login.forgot_password" defaultValue="Forgot Password" />
                  </Button>
                </div>
                <div className="relative">
                  <ValidatedInput
                    id="auth-login-password"
                    name="password"
                    type={showLoginPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder={lAuth('login.password.placeholder', { defaultValue: 'Enter password' })}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    onBlur={(e) => {
                      const result = passwordSchema.safeParse(e.target.value);
                      setLoginErrors((prev) => ({
                        ...prev,
                        password: result.success ? '' : result.error.errors[0].message,
                      }));
                    }}
                    disabled={isLoading}
                    error={loginErrors.password}
                    required
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-10 w-10 px-3 hover:bg-transparent"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    tabIndex={-1}
                  >
                    {showLoginPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="auth-keep-signed-in"
                  checked={keepMeSignedIn}
                  onCheckedChange={(checked) => setKeepMeSignedIn(!!checked)}
                />
                <Label htmlFor="auth-keep-signed-in" className="text-sm font-normal cursor-pointer">
                  <LAuth id="login.keep_signed_in" defaultValue="Keep me signed in" />
                </Label>
              </div>

              {loginFormError && (
                <Alert variant="destructive" className="py-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">{loginFormError}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <LAuth id="login.logging_in" defaultValue="Signing In" />
                  </>
                ) : (
                  <LAuth id="login.button" defaultValue="Sign In" />
                )}
              </Button>
            </form>

            {/* Password Reset Dialog */}
            <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
              <DialogContent className={DIALOGS.small}>
                <DialogHeader>
                  <DialogTitle>
                    <LAuth id="reset.title" defaultValue="Password Reset Request" />
                  </DialogTitle>
                  <DialogDescription>
                    <LAuth id="reset.description" defaultValue="Enter your email address to receive a password reset link." />
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handlePasswordReset} className={cn(SPACING.stackLg)}>
                  <div className={cn(SPACING.formFieldGap)}>
                    <Label htmlFor="auth-reset-email">
                      <LAuth id="reset.email.label" defaultValue="Email" />
                    </Label>
                    <ValidatedInput
                      id="auth-reset-email"
                      type="email"
                      placeholder={lAuth('reset.email.placeholder', { defaultValue: 'Enter email' })}
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      onBlur={(e) => {
                        const result = emailSchema.safeParse(e.target.value);
                        setResetError(result.success ? '' : result.error.errors[0].message);
                      }}
                      disabled={isResetLoading}
                      error={resetError}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isResetLoading}>
                    {isResetLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        <LCommon id="status.sending" defaultValue="Sending" />
                      </>
                    ) : (
                      <LAuth id="reset.send_link" defaultValue="Send Password Reset Link" />
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* SIGNUP TAB */}
          <TabsContent value="signup" className={TABS.contentTopLevel}>
            <div onClick={storeSessionBeforeAuth}>
              <SocialLoginButtons className="mb-2" />
            </div>

            <form onSubmit={handleSignup} className={cn(SPACING.stackLg)}>
              <div className={cn(SPACING.formFieldGap)}>
                <Label htmlFor="auth-signup-name">
                  <LAuth id="signup.full_name.label" defaultValue="Full Name" />
                </Label>
                <ValidatedInput
                  id="auth-signup-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  placeholder={lAuth('signup.full_name.placeholder', { defaultValue: 'Enter full name' })}
                  value={signupFullName}
                  onChange={(e) => setSignupFullName(e.target.value)}
                  onBlur={(e) => {
                    const result = nameSchema.safeParse(e.target.value);
                    setSignupErrors((prev) => ({
                      ...prev,
                      name: result.success ? '' : result.error.errors[0].message,
                    }));
                  }}
                  disabled={isLoading}
                  error={signupErrors.name}
                  required
                />
              </div>

              <div className={cn(SPACING.formFieldGap)}>
                <Label htmlFor="auth-signup-email">
                  <LAuth id="signup.email.label" defaultValue="Email" />
                </Label>
                <ValidatedInput
                  id="auth-signup-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder={lAuth('signup.email.placeholder', { defaultValue: 'Enter email' })}
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  onBlur={(e) => {
                    const result = emailSchema.safeParse(e.target.value);
                    setSignupErrors((prev) => ({
                      ...prev,
                      email: result.success ? '' : result.error.errors[0].message,
                    }));
                  }}
                  disabled={isLoading}
                  error={signupErrors.email}
                  required
                />
              </div>

              <div className={cn(SPACING.formFieldGap)}>
                <Label htmlFor="auth-signup-password">
                  <LAuth id="signup.password.label" defaultValue="Password" />
                </Label>
                <div className="relative">
                  <ValidatedInput
                    id="auth-signup-password"
                    name="new-password"
                    type={showSignupPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder={lAuth('signup.password.placeholder', { defaultValue: 'Enter password' })}
                    value={signupPassword}
                    onChange={(e) => {
                      setSignupPassword(e.target.value);
                      if (!signupPasswordTouched) setSignupPasswordTouched(true);
                    }}
                    onBlur={(e) => {
                      const result = passwordSchema.safeParse(e.target.value);
                      setSignupErrors((prev) => ({
                        ...prev,
                        password: result.success ? '' : result.error.errors[0].message,
                      }));
                    }}
                    disabled={isLoading}
                    error={signupErrors.password}
                    required
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-10 w-10 px-3 hover:bg-transparent"
                    onClick={() => setShowSignupPassword(!showSignupPassword)}
                    tabIndex={-1}
                  >
                    {showSignupPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                {signupPasswordTouched && (
                  <>
                    <PasswordStrengthIndicator password={signupPassword} className="mt-2" />
                    {!signupErrors.password && signupPassword.length > 0 && signupPassword.length < 6 && (
                      <p className={cn(TYPOGRAPHY.caption, 'mt-2')}>
                        <LAuth id="signup.password.min_char.hint" defaultValue="Your password must contain at least 8 characters." />
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Terms Agreement */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="auth-agree-terms"
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
                />
                <label
                  htmlFor="auth-agree-terms"
                  className={cn(TYPOGRAPHY.cardDescription, 'cursor-pointer select-none')}
                >
                  <LAuth id="signup.legal.agree_prefix" defaultValue="Agree to the" />{' '}
                  <button
                    type="button"
                    onClick={() => setTermsModalOpen(true)}
                    className="text-primary hover:underline font-medium"
                  >
                    <LAuth id="signup.legal.terms_of_use" defaultValue="Terms of Use" />
                  </button>{' '}
                  <LAuth id="signup.legal.and" defaultValue="and" />{' '}
                  <button
                    type="button"
                    onClick={() => setPrivacyModalOpen(true)}
                    className="text-primary hover:underline font-medium"
                  >
                    <LAuth id="signup.legal.privacy_policy" defaultValue="Privacy Policy" />
                  </button>
                </label>
              </div>

              {signupFormError && (
                <Alert variant="destructive" className="py-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">{signupFormError}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={isLoading || !agreedToTerms}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    <LAuth id="signup.creating_account" defaultValue="Creating your account..." />
                  </>
                ) : (
                  <LAuth id="signup.button" defaultValue="Create Account" />
                )}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </Card>

      {/* Legal Document Modals */}
      <LegalDocumentModal slug="terms-of-use" open={termsModalOpen} onOpenChange={setTermsModalOpen} />
      <LegalDocumentModal slug="privacy-policy" open={privacyModalOpen} onOpenChange={setPrivacyModalOpen} />
    </>
  );
}
